import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "as", "className", "display", "flexDirection", "alignItems", "justifyContent", "backgroundColor", "borderColor", "borderStyle", "borderWidth", "borderRadius", "padding", "paddingBlock", "paddingInline", "position", "height", "overflow", "width", "UNSAFE_style", "testId"];

/** @jsx jsx */
import { forwardRef } from 'react';
import { css, jsx } from '@emotion/react';
import { SurfaceContext } from './surface-provider';

/**
 * __Box__
 *
 * Box is a primitive component that has the design decisions of the Atlassian Design System baked in.
 * Renders a `div` by default.
 *
 * @internal
 */
export var Box = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      as = _ref.as,
      className = _ref.className,
      _ref$display = _ref.display,
      display = _ref$display === void 0 ? 'flex' : _ref$display,
      flexDirection = _ref.flexDirection,
      alignItems = _ref.alignItems,
      justifyContent = _ref.justifyContent,
      backgroundColor = _ref.backgroundColor,
      borderColor = _ref.borderColor,
      borderStyle = _ref.borderStyle,
      borderWidth = _ref.borderWidth,
      borderRadius = _ref.borderRadius,
      padding = _ref.padding,
      paddingBlock = _ref.paddingBlock,
      paddingInline = _ref.paddingInline,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? 'relative' : _ref$position,
      height = _ref.height,
      overflow = _ref.overflow,
      width = _ref.width,
      UNSAFE_style = _ref.UNSAFE_style,
      testId = _ref.testId,
      htmlAttributes = _objectWithoutProperties(_ref, _excluded);

  var Component = as || 'div';
  var node = jsx(Component, _extends({
    style: UNSAFE_style,
    ref: ref // eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props

  }, htmlAttributes, {
    className: className,
    css: [baseStyles, display && displayMap[display], padding && paddingMap[padding], position && positionMap[position], paddingBlock && paddingBlockMap[paddingBlock], paddingInline && paddingInlineMap[paddingInline], alignItems && flexAlignItemsMap[alignItems], justifyContent && flexJustifyContentMap[justifyContent], backgroundColor && backgroundColorMap[backgroundColor], borderColor && borderColorMap[borderColor], borderStyle && borderStyleMap[borderStyle], borderWidth && borderWidthMap[borderWidth], borderRadius && borderRadiusMap[borderRadius], flexDirection && flexDirectionMap[flexDirection], overflow && overflowMap[overflow], width && widthMap[width], height && heightMap[height]],
    "data-testid": testId
  }), children);
  return backgroundColor ? jsx(SurfaceContext.Provider, {
    value: backgroundColor
  }, node) : node;
});
Box.displayName = 'Box';
export default Box; // <<< STYLES GO HERE >>>

var borderStyleMap = {
  none: css({
    borderStyle: 'none'
  }),
  solid: css({
    borderStyle: 'solid'
  }),
  dashed: css({
    borderStyle: 'dashed'
  }),
  dotted: css({
    borderStyle: 'dotted'
  })
};
var borderWidthMap = {
  '0px': css({
    borderWidth: '0px'
  }),
  '1px': css({
    borderWidth: '1px'
  }),
  '2px': css({
    borderWidth: '2px'
  }),
  '3px': css({
    borderWidth: '3px'
  })
};
var borderRadiusMap = {
  normal: css({
    borderRadius: '3px'
  }),
  rounded: css({
    borderRadius: '50%'
  }),
  badge: css({
    borderRadius: '8px'
  })
};
var flexDirectionMap = {
  column: css({
    flexDirection: 'column'
  }),
  row: css({
    flexDirection: 'row'
  })
};
var flexAlignItemsMap = {
  center: css({
    alignItems: 'center'
  }),
  baseline: css({
    alignItems: 'baseline'
  }),
  flexStart: css({
    alignItems: 'flex-start'
  }),
  flexEnd: css({
    alignItems: 'flex-end'
  })
};
var flexJustifyContentMap = {
  center: css({
    justifyContent: 'center'
  }),
  flexStart: css({
    justifyContent: 'flex-start'
  }),
  flexEnd: css({
    justifyContent: 'flex-end'
  })
};
var displayMap = {
  block: css({
    display: 'block'
  }),
  inline: css({
    display: 'inline'
  }),
  flex: css({
    display: 'flex'
  }),
  inlineFlex: css({
    display: 'inline-flex'
  })
};
var positionMap = {
  absolute: css({
    position: 'absolute'
  }),
  relative: css({
    position: 'relative'
  }),
  static: css({
    position: 'static'
  })
};
var overflowMap = {
  auto: css({
    overflow: 'auto'
  })
};
var baseStyles = css({
  boxSizing: 'border-box',
  appearance: 'none',
  border: 'none'
});
/**
 * THIS SECTION WAS CREATED VIA CODEGEN DO NOT MODIFY {@see http://go/af-codegen}
 * @codegen <<SignedSource::2d25a493458fa1cefdafcd559404f2ec>>
 * @codegenId spacing
 * @codegenCommand yarn codegen-styles
 * @codegenParams ["padding", "paddingBlock", "paddingInline", "width", "height"]
 * @codegenDependency ../../../tokens/src/artifacts/tokens-raw/atlassian-light.tsx <<SignedSource::07ef29d58a2b23af8b098515466d7e22>>
 */

var paddingMap = {
  'scale.0': css({
    padding: "var(--ds-scale-0, 0px)"
  }),
  'scale.025': css({
    padding: "var(--ds-scale-025, 2px)"
  }),
  'scale.050': css({
    padding: "var(--ds-scale-050, 4px)"
  }),
  'scale.075': css({
    padding: "var(--ds-scale-075, 6px)"
  }),
  'scale.100': css({
    padding: "var(--ds-scale-100, 8px)"
  }),
  'scale.150': css({
    padding: "var(--ds-scale-150, 12px)"
  }),
  'scale.200': css({
    padding: "var(--ds-scale-200, 16px)"
  }),
  'scale.250': css({
    padding: "var(--ds-scale-250, 20px)"
  }),
  'scale.300': css({
    padding: "var(--ds-scale-300, 24px)"
  }),
  'scale.400': css({
    padding: "var(--ds-scale-400, 32px)"
  }),
  'scale.500': css({
    padding: "var(--ds-scale-500, 40px)"
  }),
  'scale.600': css({
    padding: "var(--ds-scale-600, 48px)"
  })
};
var paddingBlockMap = {
  'scale.0': css({
    paddingBlock: "var(--ds-scale-0, 0px)"
  }),
  'scale.025': css({
    paddingBlock: "var(--ds-scale-025, 2px)"
  }),
  'scale.050': css({
    paddingBlock: "var(--ds-scale-050, 4px)"
  }),
  'scale.075': css({
    paddingBlock: "var(--ds-scale-075, 6px)"
  }),
  'scale.100': css({
    paddingBlock: "var(--ds-scale-100, 8px)"
  }),
  'scale.150': css({
    paddingBlock: "var(--ds-scale-150, 12px)"
  }),
  'scale.200': css({
    paddingBlock: "var(--ds-scale-200, 16px)"
  }),
  'scale.250': css({
    paddingBlock: "var(--ds-scale-250, 20px)"
  }),
  'scale.300': css({
    paddingBlock: "var(--ds-scale-300, 24px)"
  }),
  'scale.400': css({
    paddingBlock: "var(--ds-scale-400, 32px)"
  }),
  'scale.500': css({
    paddingBlock: "var(--ds-scale-500, 40px)"
  }),
  'scale.600': css({
    paddingBlock: "var(--ds-scale-600, 48px)"
  })
};
var paddingInlineMap = {
  'scale.0': css({
    paddingInline: "var(--ds-scale-0, 0px)"
  }),
  'scale.025': css({
    paddingInline: "var(--ds-scale-025, 2px)"
  }),
  'scale.050': css({
    paddingInline: "var(--ds-scale-050, 4px)"
  }),
  'scale.075': css({
    paddingInline: "var(--ds-scale-075, 6px)"
  }),
  'scale.100': css({
    paddingInline: "var(--ds-scale-100, 8px)"
  }),
  'scale.150': css({
    paddingInline: "var(--ds-scale-150, 12px)"
  }),
  'scale.200': css({
    paddingInline: "var(--ds-scale-200, 16px)"
  }),
  'scale.250': css({
    paddingInline: "var(--ds-scale-250, 20px)"
  }),
  'scale.300': css({
    paddingInline: "var(--ds-scale-300, 24px)"
  }),
  'scale.400': css({
    paddingInline: "var(--ds-scale-400, 32px)"
  }),
  'scale.500': css({
    paddingInline: "var(--ds-scale-500, 40px)"
  }),
  'scale.600': css({
    paddingInline: "var(--ds-scale-600, 48px)"
  })
};
var widthMap = {
  'scale.0': css({
    width: "var(--ds-scale-0, 0px)"
  }),
  'scale.025': css({
    width: "var(--ds-scale-025, 2px)"
  }),
  'scale.050': css({
    width: "var(--ds-scale-050, 4px)"
  }),
  'scale.075': css({
    width: "var(--ds-scale-075, 6px)"
  }),
  'scale.100': css({
    width: "var(--ds-scale-100, 8px)"
  }),
  'scale.150': css({
    width: "var(--ds-scale-150, 12px)"
  }),
  'scale.200': css({
    width: "var(--ds-scale-200, 16px)"
  }),
  'scale.250': css({
    width: "var(--ds-scale-250, 20px)"
  }),
  'scale.300': css({
    width: "var(--ds-scale-300, 24px)"
  }),
  'scale.400': css({
    width: "var(--ds-scale-400, 32px)"
  }),
  'scale.500': css({
    width: "var(--ds-scale-500, 40px)"
  }),
  'scale.600': css({
    width: "var(--ds-scale-600, 48px)"
  })
};
var heightMap = {
  'scale.0': css({
    height: "var(--ds-scale-0, 0px)"
  }),
  'scale.025': css({
    height: "var(--ds-scale-025, 2px)"
  }),
  'scale.050': css({
    height: "var(--ds-scale-050, 4px)"
  }),
  'scale.075': css({
    height: "var(--ds-scale-075, 6px)"
  }),
  'scale.100': css({
    height: "var(--ds-scale-100, 8px)"
  }),
  'scale.150': css({
    height: "var(--ds-scale-150, 12px)"
  }),
  'scale.200': css({
    height: "var(--ds-scale-200, 16px)"
  }),
  'scale.250': css({
    height: "var(--ds-scale-250, 20px)"
  }),
  'scale.300': css({
    height: "var(--ds-scale-300, 24px)"
  }),
  'scale.400': css({
    height: "var(--ds-scale-400, 32px)"
  }),
  'scale.500': css({
    height: "var(--ds-scale-500, 40px)"
  }),
  'scale.600': css({
    height: "var(--ds-scale-600, 48px)"
  })
};

/**
 * @codegenEnd
 */

/**
 * THIS SECTION WAS CREATED VIA CODEGEN DO NOT MODIFY {@see http://go/af-codegen}
 * @codegen <<SignedSource::781636715c2bee941d6836a5a90fed5b>>
 * @codegenId colors
 * @codegenCommand yarn codegen-styles
 * @codegenParams ["border", "background"]
 * @codegenDependency ../../../tokens/src/artifacts/tokens-raw/atlassian-light.tsx <<SignedSource::07ef29d58a2b23af8b098515466d7e22>>
 */
var borderColorMap = {
  'color.border': css({
    borderColor: "var(--ds-border, #091e4221)"
  }),
  bold: css({
    borderColor: "var(--ds-border-bold, #344563)"
  }),
  inverse: css({
    borderColor: "var(--ds-border-inverse, #FFFFFF)"
  }),
  focused: css({
    borderColor: "var(--ds-border-focused, #4C9AFF)"
  }),
  input: css({
    borderColor: "var(--ds-border-input, #FAFBFC)"
  }),
  disabled: css({
    borderColor: "var(--ds-border-disabled, #FAFBFC)"
  }),
  brand: css({
    borderColor: "var(--ds-border-brand, #0052CC)"
  }),
  selected: css({
    borderColor: "var(--ds-border-selected, #0052CC)"
  }),
  danger: css({
    borderColor: "var(--ds-border-danger, #FF5630)"
  }),
  warning: css({
    borderColor: "var(--ds-border-warning, #FFC400)"
  }),
  success: css({
    borderColor: "var(--ds-border-success, #00875A)"
  }),
  discovery: css({
    borderColor: "var(--ds-border-discovery, #998DD9)"
  }),
  information: css({
    borderColor: "var(--ds-border-information, #0065FF)"
  })
};
var backgroundColorMap = {
  disabled: css({
    backgroundColor: "var(--ds-background-disabled, #091e4289)"
  }),
  'inverse.subtle': css({
    backgroundColor: "var(--ds-background-inverse-subtle, #00000029)"
  }),
  input: css({
    backgroundColor: "var(--ds-background-input, #FAFBFC)"
  }),
  neutral: css({
    backgroundColor: "var(--ds-background-neutral, #DFE1E6)"
  }),
  'neutral.subtle': css({
    backgroundColor: "var(--ds-background-neutral-subtle, transparent)"
  }),
  'neutral.bold': css({
    backgroundColor: "var(--ds-background-neutral-bold, #42526E)"
  }),
  'brand.bold': css({
    backgroundColor: "var(--ds-background-brand-bold, #0052CC)"
  }),
  selected: css({
    backgroundColor: "var(--ds-background-selected, #DEEBFF)"
  }),
  'selected.bold': css({
    backgroundColor: "var(--ds-background-selected-bold, #0052CC)"
  }),
  danger: css({
    backgroundColor: "var(--ds-background-danger, #FFEBE6)"
  }),
  'danger.bold': css({
    backgroundColor: "var(--ds-background-danger-bold, #DE350B)"
  }),
  warning: css({
    backgroundColor: "var(--ds-background-warning, #FFFAE6)"
  }),
  'warning.bold': css({
    backgroundColor: "var(--ds-background-warning-bold, #FFAB00)"
  }),
  success: css({
    backgroundColor: "var(--ds-background-success, #E3FCEF)"
  }),
  'success.bold': css({
    backgroundColor: "var(--ds-background-success-bold, #00875A)"
  }),
  discovery: css({
    backgroundColor: "var(--ds-background-discovery, #EAE6FF)"
  }),
  'discovery.bold': css({
    backgroundColor: "var(--ds-background-discovery-bold, #5243AA)"
  }),
  information: css({
    backgroundColor: "var(--ds-background-information, #DEEBFF)"
  }),
  'information.bold': css({
    backgroundColor: "var(--ds-background-information-bold, #0052CC)"
  }),
  'color.blanket': css({
    backgroundColor: "var(--ds-blanket, #091e4289)"
  }),
  'color.blanket.selected': css({
    backgroundColor: "var(--ds-blanket-selected, #388BFF14)"
  }),
  'color.blanket.danger': css({
    backgroundColor: "var(--ds-blanket-danger, #EF5C4814)"
  }),
  'elevation.surface': css({
    backgroundColor: "var(--ds-surface, #FFFFFF)"
  }),
  'elevation.surface.sunken': css({
    backgroundColor: "var(--ds-surface-sunken, #F4F5F7)"
  }),
  'elevation.surface.raised': css({
    backgroundColor: "var(--ds-surface-raised, #FFFFFF)"
  }),
  'elevation.surface.overlay': css({
    backgroundColor: "var(--ds-surface-overlay, #FFFFFF)"
  })
};
/**
 * @codegenEnd
 */